import { createFileRoute, redirect, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { fetchMe } from '@/lib/auth';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BlurText, GradientText, MagicRings, ShinyText } from '@/components/anim';
import { emitLoginEvent } from '@/hooks/useActivityTracker';
import { humanizeApiError } from '@/lib/errorMessages';

// Batch ZT (Task 1) — accept `?reason=idle` or `?reason=server` so the
// page can surface a friendly "you were logged out because…" banner.
interface LoginSearch {
  reason?: 'idle' | 'server';
}

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): LoginSearch => {
    if (s.reason === 'idle' || s.reason === 'server') return { reason: s.reason };
    return {};
  },
  beforeLoad: async ({ search }) => {
    const me = await fetchMe();
    // Don't bounce back to /dashboard if we came here from an idle logout —
    // the user clicked nothing, they were forced out. Show login fresh.
    if (me && !search.reason) throw redirect({ to: '/dashboard' });
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [mode, setModeState] = useState<'agent' | 'admin'>('agent');
  const [agentId, setAgentId] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // Batch ZT (Task 1) — sticky banner when we arrived here due to a
  // server-side idle invalidation OR frontend countdown expiry. The
  // toast is fast and easy to miss; this banner stays until the user
  // logs back in.
  const idleBanner =
    search.reason === 'idle'
      ? t('idle.banner.idle', 'Sie wurden wegen Inaktivität abgemeldet. Bitte erneut anmelden.')
      : search.reason === 'server'
        ? t('idle.banner.server', 'Ihre Sitzung ist abgelaufen. Bitte erneut anmelden.')
        : null;

  // Switching modes always clears the password so the agent form shows
  // a single field again after a user has bounced through the admin tab
  // (otherwise the password input lingers because the field renders
  // whenever `password` is truthy).
  const setMode = (next: 'agent' | 'admin') => {
    setPassword('');
    setErr(null);
    setModeState(next);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      const payload: { agentId: string; password?: string } = { agentId: agentId.trim() };
      if (mode === 'admin' || password.trim()) payload.password = password;
      await login.mutateAsync(payload);
      emitLoginEvent();
      // Ask the browser to remember the credentials BEFORE we route away.
      // A password manager normally offers this off a real form submission +
      // page navigation; this login preventDefault()s and routes client-side,
      // so Chromium never sees one and silently skips the prompt no matter how
      // correct the autocomplete markup is. Storing the credential explicitly
      // is the supported way to ask for it in a SPA.
      await offerCredentialSave(payload.agentId, payload.password);
      await navigate({ to: '/dashboard' });
    } catch (cause) {
      // loginAsAgent throws the raw API code (invalid_admin_credentials,
      // agent_not_found, agent_inactive, rate_limit_exceeded …). Show the actual
      // reason as a sentence instead — a bare code told the user nothing.
      setErr(
        humanizeApiError(
          (cause as Error).message,
          t,
          t('errors.login_failed', 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.'),
        ),
      );
    }
  };

  return (
    <main className="grid min-h-screen grid-cols-1 bg-paper text-ink lg:grid-cols-[1.1fr_1fr]">
      {/* ─── Left: brand panel ─── */}
      <BrandPanel />

      {/* ─── Right: form panel ─── */}
      <section className="relative flex min-h-screen flex-col px-6 py-10 sm:px-10 lg:px-16">
        <div className="absolute end-6 top-6 z-10 flex items-center gap-2 sm:end-10 lg:end-16">
          <ThemeToggle />
          <LanguageSwitcher />
        </div>

        <div className="absolute start-6 top-6 sm:start-10 lg:start-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-medium uppercase text-ink-3 tracking-[0.1em] transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('auth.login.back', 'Zur Startseite')}
          </Link>
        </div>

        {/* Mobile brand mark — only on small screens (left panel hides) */}
        <div className="mt-12 flex flex-col items-center gap-2 lg:hidden">
          <img
            src="/brand/logo-full.png"
            alt="4M Immobilien"
            style={{ height: 56, width: 'auto', objectFit: 'contain', display: 'block' }}
          />
          <p className="text-[11px] uppercase text-ink-3 tracking-[0.12em]">
            {t('auth.login.portal', 'Mitarbeiter-Portal')}
          </p>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="w-full max-w-md">
            <BlurText as="h2" className="block font-display text-3xl leading-tight text-ink sm:text-4xl">
              {t('auth.login.title_lead', 'Willkommen')}
            </BlurText>
            <BlurText
              delay={250}
              as="span"
              className="block font-display text-3xl italic leading-tight sm:text-4xl"
            >
              <GradientText
                animate
                from="hsl(var(--color-primary))"
                via="hsl(var(--brand-gold))"
                to="hsl(var(--color-primary))"
              >
                {t('auth.login.title_accent', 'zurück.')}
              </GradientText>
            </BlurText>

            <p className="mt-4 text-sm leading-relaxed text-ink-2">
              {t(
                'auth.login.subtitle',
                'Wählen Sie Ihre Rolle und melden Sie sich mit Ihrem 4M-Konto an.',
              )}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-1 rounded-md bg-paper-2 p-1">
              <ModeButton active={mode === 'agent'} onClick={() => setMode('agent')}>
                {t('auth.login.tab_agent', 'Makler')}
              </ModeButton>
              <ModeButton active={mode === 'admin'} onClick={() => setMode('admin')}>
                {t('auth.login.tab_admin', 'Admin')}
              </ModeButton>
            </div>

            {idleBanner && (
              <div className="mt-4 rounded-md border border-[hsl(var(--brand-gold)/0.4)] bg-[hsl(var(--brand-gold)/0.08)] px-4 py-3 text-sm text-ink">
                {idleBanner}
              </div>
            )}
            <form className="mt-6 space-y-4" onSubmit={onSubmit}>
              <Field
                label={
                  mode === 'agent'
                    ? t('auth.login.agent_id_or_email', 'Agent-ID oder E-Mail')
                    : t('auth.login.username', 'Benutzername')
                }
                name="username"
                value={agentId}
                onChange={setAgentId}
                placeholder={mode === 'agent' ? 'M001 · name@4m-immo.at' : 'admin1'}
                autoComplete="username"
                mono={mode === 'admin' || !agentId.includes('@')}
                required
              />
              {/* Password field is ALWAYS rendered (both modes) so the browser
                  recognises the form as credential-saveable and offers "Save
                  password / Use saved password" on the makler/CC flow too —
                  before this change the password field was hidden behind a
                  toggle button and browsers never saw it on initial load. */}
              <Field
                label={t('auth.login.password', 'Passwort')}
                name="password"
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="current-password"
                // E-mail login has no default-password fallback server-side,
                // so require the field as soon as the identifier is an address.
                required={mode === 'admin' || agentId.includes('@')}
                placeholder={
                  mode === 'agent' && !agentId.includes('@')
                    ? t('auth.login.password_optional_hint', 'Leer lassen für Erst-Login (= Agent-ID in Großbuchstaben)')
                    : undefined
                }
              />

              {err && (
                <p className="rounded-md border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] px-3 py-2 text-sm text-[hsl(var(--danger))]">
                  {err}
                </p>
              )}

              <button
                type="submit"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-ink px-4 py-3 font-semibold text-paper shadow-soft transition-all hover:bg-ink/90 disabled:opacity-60"
                disabled={login.isPending}
              >
                {login.isPending
                  ? t('auth.login.submitting', 'Anmelden …')
                  : t('auth.login.submit', 'Anmelden')}
              </button>
            </form>

            <div className="mt-7 flex items-start gap-2 rounded-md border border-line bg-paper-2/60 p-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-3" />
              <p className="text-[11.5px] leading-relaxed text-ink-3">
                {t(
                  'auth.login.compliance',
                  'Geschlossener Bereich. Nur autorisierte Mitarbeiter:innen. Alle Anmeldungen werden gemäß DSGVO und ISO 27001 protokolliert.',
                )}
              </p>
            </div>
          </div>
        </div>

        <p className="mt-8 text-center text-[11px] uppercase text-ink-3 tracking-[0.12em]">
          4 M Immobilien CRM v2.0 · Wien
        </p>
      </section>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Brand panel — visible on lg+, shows quote + signature
// ─────────────────────────────────────────────────────────────────────────
function BrandPanel() {
  const { t } = useTranslation();
  return (
    <aside
      className="relative hidden flex-col justify-between overflow-hidden border-e border-line p-12 lg:flex"
      style={{
        background:
          'linear-gradient(135deg, hsl(var(--paper)) 0%, hsl(var(--paper-2)) 100%)',
      }}
    >
      {/* Decorative magic rings */}
      <MagicRings
        size={780}
        rings={6}
        opacity={0.16}
        style={{ bottom: '-30%', left: '-20%' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(70% 60% at 30% 70%, hsl(var(--color-primary) / 0.05) 0%, transparent 65%)',
        }}
      />

      <div className="relative z-10 flex flex-col gap-2">
        <img
          src="/brand/logo-full.png"
          alt="4M Immobilien"
          style={{ height: 64, width: 'auto', objectFit: 'contain', display: 'block' }}
        />
        <p className="text-[11px] font-medium uppercase text-ink-3 tracking-[0.12em]">
          {t('auth.login.portal', 'Mitarbeiter-Portal · v2.0')}
        </p>
      </div>

      <div className="relative z-10 max-w-lg">
        <p className="text-[11px] font-medium uppercase text-ink-3 tracking-[0.16em]">
          <ShinyText speed={5}>{t('auth.login.quote_eyebrow', 'Aus dem Büro')}</ShinyText>
        </p>
        <blockquote className="mt-4 font-display text-3xl italic leading-tight text-ink">
          „
          {t(
            'auth.login.quote',
            'Eine internationale Immobilienagentur, die den strengsten Standards gerecht wird.',
          )}
          "
        </blockquote>
        <div className="mt-6 flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full font-display text-base text-white"
            style={{
              background: 'linear-gradient(135deg, hsl(var(--color-primary)), hsl(var(--brand-gold-deep)))',
            }}
            aria-hidden
          >
            ZK
          </div>
          <div>
            <p className="text-sm font-semibold text-ink">
              {t('auth.login.quote_author', 'Zoran Kalabić')}
            </p>
            <p className="text-xs text-ink-3">
              {t('auth.login.quote_author_title', 'Dipl. Oec., MBA · Founder / Gründer')}
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-10 flex items-end justify-between gap-4 text-[11px] uppercase text-ink-3 tracking-[0.1em]">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--positive))] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--positive))]" />
          </span>
          {t('auth.login.system_online', 'System online · Wien')}
        </div>
        <p>DSGVO · ISO 27001 · ImmoWAG</p>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Credential save ("Passwort speichern?")
// ─────────────────────────────────────────────────────────────────────────
// Chromium (desktop + Android) exposes the Credential Management API, which
// lets a single-page app request the save prompt that a classic form POST
// would have triggered on its own. Safari and Firefox do not implement
// PasswordCredential — they fall back to their own heuristics, which the
// form's autocomplete="username" / "current-password" markup already feeds.
// Every failure mode here is non-fatal: the user is already logged in, so a
// refused or unsupported save must never surface as an error.
async function offerCredentialSave(id: string, password?: string): Promise<void> {
  if (!password) return; // first-login flow submits an empty field — nothing to save
  try {
    const ctor = (window as unknown as { PasswordCredential?: new (data: {
      id: string;
      password: string;
    }) => Credential }).PasswordCredential;
    if (!ctor || !navigator.credentials?.store) return;
    if (!window.isSecureContext) return; // API is HTTPS-only; localhost counts
    // Race a timeout: the spec does not promise store() settles without user
    // interaction, and this sits between a SUCCESSFUL login and the redirect.
    // An unsettled promise would strand the user on the login screen even
    // though they are already authenticated — the save prompt is a nicety, the
    // redirect is not, so the redirect must never wait on it. The prompt itself
    // is browser chrome and survives the client-side route change.
    await Promise.race([
      navigator.credentials.store(new ctor({ id, password })),
      new Promise((resolve) => window.setTimeout(resolve, 1500)),
    ]);
  } catch {
    // Browser declined, user dismissed, or the API is gated — ignore.
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Form primitives
// ─────────────────────────────────────────────────────────────────────────
function Field({
  label,
  value,
  onChange,
  type = 'text',
  mono = false,
  ...rest
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  mono?: boolean;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  name?: string;
}) {
  const { t } = useTranslation();
  // Eye toggle for password fields — industry-standard show/hide pattern.
  // Default is hidden; click eye to reveal, click again to mask.
  const isPasswordField = type === 'password';
  const [revealed, setRevealed] = useState(false);
  const effectiveType = isPasswordField && revealed ? 'text' : type;
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase text-ink-3 tracking-[0.08em]">
        {label}
      </span>
      <div className="relative">
        <input
          type={effectiveType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-md border border-line bg-card py-2.5 text-sm text-ink outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/30 ${
            isPasswordField ? 'ps-3 pe-11' : 'px-3'
          } ${mono ? 'font-mono tracking-wide' : ''}`}
          {...rest}
        />
        {isPasswordField && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setRevealed((v) => !v)}
            aria-label={
              revealed
                ? t('auth.login.hide_password', 'Passwort verbergen')
                : t('auth.login.show_password', 'Passwort anzeigen')
            }
            aria-pressed={revealed}
            className="absolute inset-y-0 end-0 flex items-center justify-center px-3 text-ink-3 transition-colors hover:text-ink focus:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 rounded-e-md"
          >
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
    </label>
  );
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-2 text-sm font-semibold transition-all ${
        active ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}
